import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Link } from 'react-router-dom';
import type { StudentPayload } from './checkInTypes';
import { ACCOUNT_BILLING_PATH } from './accountPaths';
import type { StudentLimit } from './studentLimit';

type StudentUpsertDialogProps = {
  open: boolean;
  mode: 'add' | 'edit';
  student: StudentPayload | null;
  grades: string[];
  rooms: string[];
  teachers: string[];
  /** When true, add mode cannot submit (plan cap reached). */
  atStudentLimit?: boolean;
  studentLimit?: StudentLimit;
  onClose: () => void;
  onSaved: (student: StudentPayload) => void;
};

type FieldErrors = {
  firstName?: string;
  lastName?: string;
  parentGuardian1?: string;
  parentGuardianEmail1?: string;
  parentGuardianPhone1?: string;
};

const inputClassName =
  'mt-1 block w-full rounded-xl border-2 border-ink/10 bg-white px-3 py-2 font-sans text-sm font-semibold text-ink shadow-sm focus:border-skysoft focus:outline-none focus:ring-2 focus:ring-skysoft/60';

const StudentUpsertDialog = ({
  open,
  mode,
  student,
  grades,
  rooms,
  teachers,
  atStudentLimit = false,
  studentLimit = null,
  onClose,
  onSaved,
}: StudentUpsertDialogProps) => {
  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [lastName, setLastName] = useState('');
  const [classTeacher, setClassTeacher] = useState('');
  const [grade, setGrade] = useState('');
  const [room, setRoom] = useState('');
  const [parentGuardian1, setParentGuardian1] = useState('');
  const [parentGuardian2, setParentGuardian2] = useState('');
  const [parentGuardianEmail1, setParentGuardianEmail1] = useState('');
  const [parentGuardianEmail2, setParentGuardianEmail2] = useState('');
  const [parentGuardianPhone1, setParentGuardianPhone1] = useState('');
  const [parentGuardianPhone2, setParentGuardianPhone2] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setFirstName(student?.firstName ?? '');
    setMiddleName(student?.middleName ?? '');
    setLastName(student?.lastName ?? '');
    setClassTeacher(student?.classTeacher ?? '');
    setGrade(student?.grade ?? '');
    setRoom(student?.room ?? '');
    setParentGuardian1(student?.parent_guardian_1 ?? '');
    setParentGuardian2(student?.parent_guardian_2 ?? '');
    setParentGuardianEmail1(student?.parent_guardian_email_1 ?? '');
    setParentGuardianEmail2(student?.parent_guardian_email_2 ?? '');
    setParentGuardianPhone1(student?.parent_guardian_phone_1 ?? '');
    setParentGuardianPhone2(student?.parent_guardian_phone_2 ?? '');
    setFieldErrors({});
    setError(null);
  }, [open, student]);

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (mode === 'add' && atStudentLimit) {
        setError(
          studentLimit === null
            ? 'Student limit reached. Upgrade your plan to add more students.'
            : `Student limit reached (${studentLimit}). Upgrade your plan to add more students.`,
        );
        return;
      }
      const errors: FieldErrors = {};
      if (!firstName.trim()) errors.firstName = 'First name is required.';
      if (!lastName.trim()) errors.lastName = 'Last name is required.';
      if (!parentGuardian1.trim()) {
        errors.parentGuardian1 = 'Parent / guardian 1 is required.';
      }
      if (!parentGuardianEmail1.trim()) {
        errors.parentGuardianEmail1 = 'Email 1 is required.';
      }
      if (!parentGuardianPhone1.trim()) {
        errors.parentGuardianPhone1 = 'Phone 1 is required.';
      }
      setFieldErrors(errors);
      if (Object.keys(errors).length > 0) return;

      setSaving(true);
      setError(null);
      try {
        const body = {
          firstName: firstName.trim(),
          middleName: middleName.trim(),
          lastName: lastName.trim(),
          classTeacher: classTeacher.trim(),
          grade: grade.trim(),
          room: room.trim(),
          parent_guardian_1: parentGuardian1.trim(),
          parent_guardian_2: parentGuardian2.trim(),
          parent_guardian_email_1: parentGuardianEmail1.trim(),
          parent_guardian_email_2: parentGuardianEmail2.trim(),
          parent_guardian_phone_1: parentGuardianPhone1.trim(),
          parent_guardian_phone_2: parentGuardianPhone2.trim(),
        };
        const res = await fetch(
          mode === 'edit' && student
            ? `/api/students/${student.id}`
            : '/api/students',
          {
            method: mode === 'edit' ? 'PUT' : 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          },
        );
        if (!res.ok) {
          const data = (await res.json().catch(() => null)) as { error?: string } | null;
          throw new Error(data?.error ?? `Save failed (${res.status})`);
        }
        const data = (await res.json()) as { student: StudentPayload };
        onSaved(data.student);
        onClose();
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Save failed');
      } finally {
        setSaving(false);
      }
    },
    [
      atStudentLimit,
      classTeacher,
      firstName,
      grade,
      lastName,
      middleName,
      mode,
      onClose,
      onSaved,
      parentGuardian1,
      parentGuardian2,
      parentGuardianEmail1,
      parentGuardianEmail2,
      parentGuardianPhone1,
      parentGuardianPhone2,
      room,
      student,
      studentLimit,
    ],
  );

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-[60]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-ink/30" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl rounded-3xl border-[3px] border-ink/15 bg-white p-6 shadow-[4px_8px_0_rgba(44,44,44,0.1)]">
                <Dialog.Title className="font-display text-xl font-semibold text-ink">
                  {mode === 'edit' ? 'Edit student' : 'Add student'}
                </Dialog.Title>
                {mode === 'add' && atStudentLimit ? (
                  <p
                    className="mt-3 rounded-xl bg-doghouse/15 px-3 py-2 font-sans text-sm font-semibold text-ink"
                    role="alert"
                  >
                    Student limit reached
                    {studentLimit !== null ? ` (${studentLimit})` : ''}.{' '}
                    <Link
                      to={ACCOUNT_BILLING_PATH}
                      className="underline decoration-ink/40 underline-offset-2 hover:decoration-ink"
                      onClick={onClose}
                    >
                      Upgrade billing
                    </Link>{' '}
                    to add more students.
                  </p>
                ) : null}
                <form className="mt-5 space-y-4" onSubmit={(e) => void handleSubmit(e)} noValidate>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <label className="block sm:col-span-1">
                      <span className="font-sans text-xs font-semibold uppercase tracking-wide text-ink/50">
                        First name
                      </span>
                      <input
                        className={inputClassName}
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        autoComplete="given-name"
                      />
                      {fieldErrors.firstName ? (
                        <p className="mt-1 text-xs font-semibold text-doghouse">{fieldErrors.firstName}</p>
                      ) : null}
                    </label>
                    <label className="block">
                      <span className="font-sans text-xs font-semibold uppercase tracking-wide text-ink/50">
                        Middle name
                      </span>
                      <input
                        className={inputClassName}
                        value={middleName}
                        onChange={(e) => setMiddleName(e.target.value)}
                        autoComplete="additional-name"
                      />
                    </label>
                    <label className="block sm:col-span-2">
                      <span className="font-sans text-xs font-semibold uppercase tracking-wide text-ink/50">
                        Last name
                      </span>
                      <input
                        className={inputClassName}
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        autoComplete="family-name"
                      />
                      {fieldErrors.lastName ? (
                        <p className="mt-1 text-xs font-semibold text-doghouse">{fieldErrors.lastName}</p>
                      ) : null}
                    </label>
                    <label className="block">
                      <span className="font-sans text-xs font-semibold uppercase tracking-wide text-ink/50">
                        Grade
                      </span>
                      <select
                        className={inputClassName}
                        value={grade}
                        onChange={(e) => setGrade(e.target.value)}
                      >
                        <option value="">Select grade</option>
                        {grades.map((item) => (
                          <option key={item} value={item}>
                            {item}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="block">
                      <span className="font-sans text-xs font-semibold uppercase tracking-wide text-ink/50">
                        Room
                      </span>
                      <select
                        className={inputClassName}
                        value={room}
                        onChange={(e) => setRoom(e.target.value)}
                      >
                        <option value="">Select room</option>
                        {rooms.map((item) => (
                          <option key={item} value={item}>
                            {item}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="block sm:col-span-2">
                      <span className="font-sans text-xs font-semibold uppercase tracking-wide text-ink/50">
                        Class teacher
                      </span>
                      <select
                        className={inputClassName}
                        value={classTeacher}
                        onChange={(e) => setClassTeacher(e.target.value)}
                      >
                        <option value="">Select teacher</option>
                        {teachers.map((item) => (
                          <option key={item} value={item}>
                            {item}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>

                  <div className="border-t border-ink/10 pt-4">
                    <p className="font-sans text-xs font-semibold uppercase tracking-wide text-ink/50">
                      Parent / guardian
                    </p>
                    <div className="mt-3 grid gap-4 sm:grid-cols-2">
                      <label className="block">
                        <span className="font-sans text-xs font-semibold uppercase tracking-wide text-ink/50">
                          Parent / guardian 1
                        </span>
                        <input
                          className={inputClassName}
                          value={parentGuardian1}
                          onChange={(e) => setParentGuardian1(e.target.value)}
                          autoComplete="name"
                        />
                        {fieldErrors.parentGuardian1 ? (
                          <p className="mt-1 text-xs font-semibold text-doghouse">
                            {fieldErrors.parentGuardian1}
                          </p>
                        ) : null}
                      </label>
                      <label className="block">
                        <span className="font-sans text-xs font-semibold uppercase tracking-wide text-ink/50">
                          Parent / guardian 2
                        </span>
                        <input
                          className={inputClassName}
                          value={parentGuardian2}
                          onChange={(e) => setParentGuardian2(e.target.value)}
                          autoComplete="name"
                        />
                      </label>
                      <label className="block">
                        <span className="font-sans text-xs font-semibold uppercase tracking-wide text-ink/50">
                          Email 1
                        </span>
                        <input
                          type="email"
                          className={inputClassName}
                          value={parentGuardianEmail1}
                          onChange={(e) => setParentGuardianEmail1(e.target.value)}
                          autoComplete="email"
                        />
                        {fieldErrors.parentGuardianEmail1 ? (
                          <p className="mt-1 text-xs font-semibold text-doghouse">
                            {fieldErrors.parentGuardianEmail1}
                          </p>
                        ) : null}
                      </label>
                      <label className="block">
                        <span className="font-sans text-xs font-semibold uppercase tracking-wide text-ink/50">
                          Email 2
                        </span>
                        <input
                          type="email"
                          className={inputClassName}
                          value={parentGuardianEmail2}
                          onChange={(e) => setParentGuardianEmail2(e.target.value)}
                          autoComplete="email"
                        />
                      </label>
                      <label className="block">
                        <span className="font-sans text-xs font-semibold uppercase tracking-wide text-ink/50">
                          Phone 1
                        </span>
                        <input
                          type="tel"
                          className={inputClassName}
                          value={parentGuardianPhone1}
                          onChange={(e) => setParentGuardianPhone1(e.target.value)}
                          autoComplete="tel"
                        />
                        {fieldErrors.parentGuardianPhone1 ? (
                          <p className="mt-1 text-xs font-semibold text-doghouse">
                            {fieldErrors.parentGuardianPhone1}
                          </p>
                        ) : null}
                      </label>
                      <label className="block">
                        <span className="font-sans text-xs font-semibold uppercase tracking-wide text-ink/50">
                          Phone 2
                        </span>
                        <input
                          type="tel"
                          className={inputClassName}
                          value={parentGuardianPhone2}
                          onChange={(e) => setParentGuardianPhone2(e.target.value)}
                          autoComplete="tel"
                        />
                      </label>
                    </div>
                  </div>

                  {error ? (
                    <p className="rounded-xl bg-doghouse/15 px-3 py-2 text-sm font-semibold text-ink" role="alert">
                      {error}
                    </p>
                  ) : null}

                  <div className="flex justify-end gap-3 pt-2">
                    <button
                      type="button"
                      onClick={onClose}
                      className="rounded-full px-4 py-2 text-sm font-semibold text-ink/70 hover:bg-ink/5"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={saving || (mode === 'add' && atStudentLimit)}
                      className="rounded-full bg-skysoft/70 px-5 py-2 text-sm font-semibold text-ink ring-2 ring-ink/10 hover:bg-skysoft disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {saving ? 'Saving…' : mode === 'edit' ? 'Save changes' : 'Add student'}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default StudentUpsertDialog;
